import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Compass, X } from "lucide-react";
import Photo360Viewer from "@/components/Photo360Viewer";
import { mediaProxyUrlForced } from "@/lib/media";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";

/**
 * Refugium-spezifische Sektion "360° · In jeden Raum eintreten".
 *
 * Erwartet ein Array von Media-Objekten (photo_360) aus dem Projekt und
 * gruppiert sie anhand des Media-Titels in 5 Raum-Karten mit je einem
 * Rohbau- und einem Fertig-Button. Bild-Preload beim Mount für praktisch
 * instant öffnende Modals (siehe UseCaseExampleTile-Muster).
 */

type Sky = {
    id: string;
    title?: string | null;
    stored_url?: string | null;
    media_type: string;
};

type Room = {
    key: string;
    label: string;
    rohbau: Sky | null;
    fertig: Sky | null;
};

const ROOMS: { key: string; label: string; matchRohbau: RegExp; matchFertig: RegExp }[] = [
    { key: "wohnzimmer", label: "Wohnzimmer", matchRohbau: /^Refugium\s*[-–]\s*Wohnzimmer Rohbau/i, matchFertig: /^Refugium\s*[-–]\s*Wohnzimmer Fertig/i },
    { key: "kueche", label: "Küche", matchRohbau: /^Refugium\s*[-–]\s*Küche Rohbau(?!.*Reserve)/i, matchFertig: /^Refugium\s*[-–]\s*Küche Fertig/i },
    { key: "bad", label: "Bad", matchRohbau: /^Refugium\s*[-–]\s*Bad Rohbau/i, matchFertig: /^Refugium\s*[-–]\s*Bad Fertig/i },
    { key: "schlafzimmer", label: "Schlafzimmer", matchRohbau: /^Refugium\s*[-–]\s*Schlafzimmer Rohbau/i, matchFertig: /^Refugium\s*[-–]\s*Schlafzimmer Fertig/i },
    { key: "terrasse", label: "Wohnzimmer mit Terrasse", matchRohbau: /^Refugium\s*[-–]\s*Wohnzimmer mit Terrasse Rohbau/i, matchFertig: /^Refugium\s*[-–]\s*Wohnzimmer mit Terrasse Fertig/i },
];

export default function RefugiumSkyboxSection({ media }: { media: Sky[] }) {
    const skies = media.filter((m) => m.media_type === "photo_360" && m.stored_url);

    const rooms: Room[] = ROOMS.map((r) => ({
        key: r.key,
        label: r.label,
        rohbau: skies.find((m) => r.matchRohbau.test(m.title || "")) || null,
        fertig: skies.find((m) => r.matchFertig.test(m.title || "")) || null,
    })).filter((r) => r.rohbau || r.fertig);

    const [openSky, setOpenSky] = useState<{ url: string; label: string; variant: "rohbau" | "fertig" } | null>(null);

    // Preload aller Skyboxes beim Mount — matcht Photo360Viewer-CORS
    useEffect(() => {
        rooms.forEach((r) => {
            [r.rohbau, r.fertig].forEach((s) => {
                if (!s?.stored_url) return;
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.decoding = "async";
                img.src = s.stored_url;
            });
        });
    }, [rooms]);

    // ESC-Handler + Body-Scroll-Lock
    useEffect(() => {
        if (!openSky) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpenSky(null);
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [openSky]);

    if (rooms.length === 0) return null;

    return (
        <section
            id="project-refugium-360"
            className="py-16 md:py-24"
            data-testid="refugium-360-section"
            style={{ background: "hsl(var(--hf-bg))" }}
        >
            <div className="max-w-[1280px] mx-auto px-5 md:px-8">
                <div className="mb-10">
                    <div className="hf-eyebrow">360° · Rundgang</div>
                    <h2 className="font-display text-3xl md:text-4xl mt-2">In jeden Raum eintreten.</h2>
                    <p className="mt-2 text-hf-muted text-sm md:text-base max-w-2xl">
                        Erkunden Sie jeden Raum vollständig — als Rohbau und als fertiges Refugium. Drehen, zoomen, Vollbild.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {rooms.map((r) => {
                        const preview = r.fertig?.stored_url || r.rohbau?.stored_url;
                        return (
                            <article
                                key={r.key}
                                data-testid={`refugium-360-room-${r.key}`}
                                className="group relative overflow-hidden rounded-sm"
                                style={{
                                    background: "hsl(var(--hf-surface))",
                                    border: "1px solid hsl(var(--hf-line))",
                                }}
                            >
                                <div className="relative w-full aspect-[3/2] overflow-hidden">
                                    {preview && (
                                        <img
                                            src={preview}
                                            alt={r.label}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                        />
                                    )}
                                    <div
                                        aria-hidden="true"
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            background:
                                                "linear-gradient(to bottom, rgba(14,23,38,0) 0%, rgba(14,23,38,0) 50%, rgba(14,23,38,0.5) 100%)",
                                        }}
                                    />
                                    <div className="absolute top-3 left-3">
                                        <span
                                            className="hf-eyebrow px-2.5 py-1 rounded-sm text-[10px] uppercase"
                                            style={{
                                                background: "rgba(255,255,255,0.9)",
                                                color: "hsl(var(--hf-ink))",
                                                letterSpacing: "0.14em",
                                            }}
                                        >
                                            {r.label}
                                        </span>
                                    </div>
                                </div>

                                <div className="p-5">
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            disabled={!r.rohbau?.stored_url}
                                            data-testid={`refugium-360-btn-rohbau-${r.key}`}
                                            onClick={() =>
                                                r.rohbau?.stored_url &&
                                                setOpenSky({
                                                    url: mediaProxyUrlForced(r.rohbau.id),
                                                    label: `${r.label} · Rohbau`,
                                                    variant: "rohbau",
                                                })
                                            }
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[12px] tracking-wide transition-colors disabled:opacity-40"
                                            style={{
                                                border: "1px solid hsl(var(--hf-line))",
                                                background: "hsl(var(--hf-bg))",
                                                color: "hsl(var(--hf-ink))",
                                            }}
                                        >
                                            <Compass size={14} />
                                            <span>Rohbau 360° erleben</span>
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!r.fertig?.stored_url}
                                            data-testid={`refugium-360-btn-fertig-${r.key}`}
                                            onClick={() =>
                                                r.fertig?.stored_url &&
                                                setOpenSky({
                                                    url: mediaProxyUrlForced(r.fertig.id),
                                                    label: `${r.label} · Fertig`,
                                                    variant: "fertig",
                                                })
                                            }
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[12px] tracking-wide transition-colors disabled:opacity-40"
                                            style={{
                                                border: "1px solid hsl(var(--hf-gold) / 0.55)",
                                                background: "hsl(var(--hf-gold-soft) / 0.55)",
                                                color: "hsl(var(--hf-gold-dark))",
                                            }}
                                        >
                                            <Compass size={14} />
                                            <span>Fertig 360° erleben</span>
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>

            {openSky &&
                createPortal(
                    <SkyModal
                        url={openSky.url}
                        label={openSky.label}
                        variant={openSky.variant}
                        onClose={() => setOpenSky(null)}
                    />,
                    document.body,
                )}
        </section>
    );
}

function SkyModal({
    url,
    label,
    variant,
    onClose,
}: {
    url: string;
    label: string;
    variant: "rohbau" | "fertig";
    onClose: () => void;
}) {
    const backdrop = useRef<HTMLDivElement | null>(null);
    return (
        <div
            ref={backdrop}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            data-testid="refugium-360-modal"
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-8"
            style={{ background: "rgba(14,23,38,0.72)", backdropFilter: "blur(4px)" }}
            onClick={(e) => {
                if (e.target === backdrop.current) onClose();
            }}
        >
            <div
                className="relative w-full max-w-[1280px] rounded-sm overflow-hidden"
                style={{
                    background: "hsl(var(--hf-bg))",
                    border: "1px solid hsl(var(--hf-line))",
                }}
            >
                <div
                    className="flex items-center justify-between px-4 sm:px-5 py-3 border-b"
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                >
                    <div className="min-w-0">
                        <div className="hf-eyebrow text-[10px]" style={{ color: "hsl(var(--hf-gold-dark))" }}>
                            360°-Foto · Photo Sphere
                        </div>
                        <div className="font-display text-base sm:text-lg truncate">{label}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="refugium-360-modal-close"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-sm transition-colors"
                        style={{
                            border: "1px solid hsl(var(--hf-line))",
                            color: "hsl(var(--hf-ink))",
                            background: "hsl(var(--hf-surface))",
                        }}
                        aria-label="Schließen"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-3 sm:p-4">
                    <ViewerErrorBoundary
                        fallbackTitle="360°-Ansicht nicht darstellbar"
                        fallbackMessage="Das 360°-Panorama konnte nicht geladen werden. Bitte prüfe WebGL im Browser."
                    >
                        <Photo360Viewer src={url} testIdPrefix={`refugium-360-viewer-${variant}`} />
                    </ViewerErrorBoundary>
                </div>
            </div>
        </div>
    );
}
